/**
 * useLeaderboardQueries — TanStack Query hooks over the Plan 02-08 endpoints.
 *
 * Backend (apps/api/src/http/leaderboard.routes.ts):
 *   GET /api/leaderboard/weekly    → { rows: LeaderRow[] }
 *   GET /api/leaderboard/all-time  → { rows: LeaderRow[] }
 *
 * LeaderRow wire shape (Plan 02-08 leaderboard.service.ts):
 *   { memberId, short_name, slot, points, rank }
 *
 * Cadence:
 *   - Weekly: staleTime 30s — leaderboard ranks reorder when chores get
 *     approved (WS event), but inside that window the cache is fresh.
 *     A future Plan 15 (celebrations) cache-bust on chore.approved
 *     can call qc.invalidateQueries({queryKey:['leaderboard']}) to
 *     refresh both views without a manual reload.
 *   - All-time: staleTime 60s — lifetime SUM moves slowly.
 *
 * No `enabled` gating — LEAD-01 family-visible: any authed family member
 * can read both endpoints. The cookie carries auth on every request.
 */
import { useQuery } from '@tanstack/react-query';

export interface LeaderRow {
  memberId: string;
  short_name: string;
  /** Member colour slot — same string the API returns from members.color_token. */
  slot: string;
  points: number;
  /** 1-based rank with dense-on-ties semantics (Plan 02-08). */
  rank: number;
}

interface LeaderboardBody {
  rows: LeaderRow[];
}

async function fetchJson<T>(url: string): Promise<T> {
  const r = await globalThis.fetch(url, { credentials: 'same-origin' });
  if (!r.ok) {
    const e = new Error(`fetch_failed:${r.status}`) as Error & { status?: number };
    e.status = r.status;
    throw e;
  }
  return (await r.json()) as T;
}

export function useWeeklyLeaderboard(enabled = true) {
  return useQuery({
    queryKey: ['leaderboard', 'weekly'],
    enabled,
    queryFn: async () => {
      const body = await fetchJson<LeaderboardBody>('/api/leaderboard/weekly');
      return body.rows;
    },
    staleTime: 30_000,
  });
}

export function useAllTimeLeaderboard() {
  return useQuery({
    queryKey: ['leaderboard', 'all-time'],
    queryFn: async () => {
      const body = await fetchJson<LeaderboardBody>('/api/leaderboard/all-time');
      return body.rows;
    },
    staleTime: 60_000,
  });
}
